import { State } from 'model/State';
import { fetchWithParamValidation } from 'util/fetchWithParamValidation';
import {
  getFromHamsCartClient,
  getFromHamsClient,
  getFromHamsOrderQuoteClient,
  getFromNonExternalHamsClient,
} from 'util/mac/api/order/get';
import { getFromHamsClient as getFromRefund } from 'util/mac/api/refund/get';
import { postToHamsClient as postToCart } from 'util/mac/api/cart/post';
import { postToHamsClient as postToOrdersContact } from 'util/mac/api/ordersContact/post';
import { DownloadablePDF, downloadFile } from 'util/mac/downloadFile';
import { downloadStringifiedJSONFromApi } from 'util/mac/downloadJSON';
import { postToHamsCartClient, postToNonExternalHamsClient } from 'util/mac/api/order/post';
import { deleteFromHamsClient } from 'util/mac/api/cart/delete';

import { selectSwitchedUser } from '../selectors';

export const quotes = (
  state: State,
  maxResultsParam: number,
  offsetParam: number,
  orderNumberMask?: number,
  technicalContactCompanyDomainName?: string,
  integerPartOfTotalAmount?: number,
) => {
  const email = selectSwitchedUser(state);

  return fetchWithParamValidation(
    ({ maxResults, offset }) =>
      postToOrdersContact(
        '/quotes',
        state,
        {
          maxResults,
          offset,
        },
        {
          email,
          orderNumberMask,
          technicalContactCompanyDomainName,
          integerPartOfTotalAmount,
        },
      ),
    { maxResults: maxResultsParam, offset: offsetParam },
  );
};

export const refundPDF = async (refundId: string, state: State, hash?: string) =>
  fetchWithParamValidation(
    async ({ id }) => {
      const url = hash ? `/external/${id}/pdf?hash=${hash}` : `/external/${id}/pdf`;
      const response: DownloadablePDF = await getFromRefund(url, state);

      downloadFile(response);
    },
    {
      id: refundId,
    },
  );

export const cartInvoicePdf = async (cartId: string, state: State, hash?: string) =>
  fetchWithParamValidation(
    async ({ id }) => {
      const url = hash ? `/proforma/${id}/pdf?hash=${hash}` : `/proforma/${id}/pdf`;
      const response: DownloadablePDF = await getFromHamsClient(url, state);

      downloadFile(response);
    },
    {
      id: cartId,
    },
  );

export const orderPdf = async (orderId: number, state: State, hash?: string) =>
  fetchWithParamValidation(
    async ({ id }) => {
      const url = hash ? `/${id}/quote/pdf?hash=${hash}` : `/${id}/quote/pdf`;
      const response: DownloadablePDF = await getFromHamsClient(url, state);

      downloadFile(response);
    },
    {
      id: orderId,
    },
  );

export const orderJson = async (orderId: string, state: State, hash?: string) =>
  fetchWithParamValidation(
    async ({ id }) => {
      const url = hash ? `/${id}/summary?hash=${hash}` : `/${id}/summary`;
      const response = await getFromHamsOrderQuoteClient(url, state);

      downloadStringifiedJSONFromApi(response);
    },
    {
      id: orderId,
    },
  );

export const fetchOrderCart = (cartId: string, state: State) =>
  fetchWithParamValidation(({ id }) => getFromHamsCartClient(`/${id}?_=${Date.now()}`, state), {
    id: cartId,
  });

export const cartCloneQuote = (cartId: string, state: State) =>
  fetchWithParamValidation(({ id }) => postToCart(`/quote/${id}/clone`, state), {
    id: cartId,
  });

export const deleteQuote = (cartId: string, displayName: string, state: State) => {
  const email = selectSwitchedUser(state);
  const reason = `Deleted by ${displayName} (${email}) on ${Date.now()}"`;
  return fetchWithParamValidation(
    ({ cartIdParam, reasonParam }) =>
      deleteFromHamsClient(`/${cartIdParam}?reason=${reasonParam}`, state),
    { cartIdParam: cartId, reasonParam: reason },
  );
};

export const cartCopyForEditing = (cartId: string, state: State) =>
  fetchWithParamValidation(({ id }) => postToHamsCartClient(`/${id}/copyCartForEditing`, state), {
    id: cartId,
  });

export const generateVerificationHash = (
  entityId: number,
  totalIncTax: number,
  billingContactEmail: string,
  state: State,
) =>
  fetchWithParamValidation(
    () =>
      postToNonExternalHamsClient(
        `/generateVerificationHash`,
        state,
        {},
        {
          entityId,
          totalIncTax,
          billingContactEmail,
        },
      ),
    {},
  );

export const singleOrder = (orderId: string, state: State, hash?: string) =>
  fetchWithParamValidation(
    ({ id }) => getFromNonExternalHamsClient(hash ? `/${id}?hash=${hash}` : `/${id}`, state, {}),
    { id: orderId },
  );
